import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { registerDirtySource } from '@onvice/portal-shell-api';
import { createSaveRegistry, type SaveRegistry } from './saveRegistry';

export interface UseSaveRegistryOptions {
  /** Anmeldung beim Verlassen-Schutz; fehlt sie, schützt der Aufrufer selbst */
  guard?: { id: string; label: string };
}

export interface SaveRegistryState {
  registry: SaveRegistry;
  /** Mindestens ein Teil ist geändert */
  anyDirty: boolean;
}

/**
 * Seite mit einem gemeinsamen Speichern-Knopf: legt die Sammelstelle an und liefert, ob
 * etwas zu speichern ist. Die Teile melden sich mit useSaveParticipant an.
 */
export function useSaveRegistry(opts: UseSaveRegistryOptions = {}): SaveRegistryState {
  const [registry] = useState(createSaveRegistry);
  const anyDirty = useSyncExternalStore(registry.subscribe, registry.anyDirty, registry.anyDirty);
  const guardId = opts.guard?.id;
  const guardLabel = opts.guard?.label;

  useEffect(() => {
    if (!guardId) return;
    return registerDirtySource({
      id: `registry:${guardId}`,
      label: guardLabel ?? 'Seite',
      isDirty: registry.anyDirty,
      discard: registry.discardAll,
    });
  }, [registry, guardId, guardLabel]);

  return { registry, anyDirty };
}

export interface SaveParticipantOptions {
  id: string;
  label: string;
  dirty: boolean;
  flush: () => Promise<boolean>;
  discard?: () => void;
}

/** Karte oder Abschnitt meldet sich beim gemeinsamen Speichern-Knopf der Seite an. */
export function useSaveParticipant(registry: SaveRegistry | null | undefined, opts: SaveParticipantOptions): void {
  const live = useRef(opts);
  live.current = opts;
  const { id, label, dirty } = opts;

  useEffect(() => {
    if (!registry) return;
    return registry.register({
      id,
      label,
      isDirty: () => live.current.dirty,
      flush: () => live.current.flush(),
      discard: () => live.current.discard?.(),
    });
  }, [registry, id, label]);

  useEffect(() => { registry?.touch(); }, [registry, dirty]);
}
